import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import forwardRefWithTranslation from '@/common/forwardRefWithTranslation';

const { classNames } = Mobi2Go.helpers;
const { Customer } = Mobi2Go;
const { Order } = Mobi2Go;
const { UI } = Mobi2Go;

export default forwardRefWithTranslation('order.loyalty')(
    class PunchCard extends React.Component {
        static propTypes = {
            is_showing_in_confirmation: PropTypes.bool,
            t: PropTypes.func.isRequired,
        };

        state = {
            loyalty: {
                program: {
                    title: '',
                    terms_and_conditions: '',
                    settings: {
                        description: '',
                        num_punches_needed: 0,
                    },
                    enabled: true,
                },
                state: {
                    punches: 0,
                },
            },
            pause_animation: false,
            is_order_confirmed: false,
            order_punchcard_progress: null,
            customer_is_logged_in: false,
        };

        componentDidMount = () => {
            this.updateState();
            Customer.bind({
                logIn: this.updateState,
                logOut: this.updateState,
                updateLoyalty: this.updateState,
            });
            Order.bind({
                updateProgressPhase: this.updateState,
                updateLoyalty: this.onOrderLoyaltyUpdate,
            });
        };

        componentWillUnmount = () => {
            clearTimeout(this.resetPunchAnimation.timeout);
            Customer.unbind({
                logIn: this.updateState,
                logOut: this.updateState,
                updateLoyalty: this.updateState,
            });
            Order.unbind({
                updateProgressPhase: this.updateState,
                updateLoyalty: this.onOrderLoyaltyUpdate,
            });
        };

        onOrderLoyaltyUpdate = () => {
            this.updateState();
            this.resetPunchAnimation();
        };

        resetPunchAnimation = () => {
            this.setState({ pause_animation: true });
            clearTimeout(this.resetPunchAnimation.timeout);
            this.resetPunchAnimation.timeout = setTimeout(() => {
                this.setState({ pause_animation: false });
            }, 300);
        };

        updateState = () => {
            this.setState({
                is_customer_logged_in: Customer.isLoggedIn(),
                loyalty: Customer.getLoyalty('PunchCard'),
                order_punchcard_progress: Order.getLoyalty('PunchCard'),
                is_order_confirmed: Order.getProgressPhase() === 'confirmed',
            });
        };

        clickTermsAndConditions = () => {
            UI.centeredAlert({
                title: this.props.t('terms_and_conditions'),
                body: this.props.t('terms_and_conditions_body'),
            });
        };

        getProgressMessage = () => {
            const { loyalty, order_punchcard_progress } = this.state;
            if (!loyalty || !loyalty.state) {
                return '';
            }

            const punches_earned = loyalty.state.punches;
            const punches_needed = loyalty.program.settings.num_punches_needed;
            const punches_left_to_earn = punches_needed - punches_earned;
            const rewards_earned_with_this_order = order_punchcard_progress
                ? order_punchcard_progress.rewards_earned
                : [];
            const num_punches_earned_with_this_order =
                order_punchcard_progress &&
                order_punchcard_progress.punches_earned;
            const { t } = this.props;

            const punch_label = t('punch_label');
            const punch_label_plural = t('punch_label_plural');

            if (this.state.is_order_confirmed) {
                if (rewards_earned_with_this_order.length) {
                    return t('reward_earned', {
                        reward_label: t('customer.rewards:label'),
                    });
                }
                if (num_punches_earned_with_this_order) {
                    return t('earned_punches', {
                        count: num_punches_earned_with_this_order,
                        punch_label,
                        punch_label_plural,
                        punches_left_to_earn,
                    });
                }
                return t('remaining_punches', {
                    count: punches_left_to_earn,
                    punch_label,
                    punch_label_plural,
                });
            }

            if (rewards_earned_with_this_order.length) {
                return t('will_earn_you', {
                    reward_label: t('customer.rewards:label'),
                });
            }
            if (num_punches_earned_with_this_order) {
                return t('earn_with_order', {
                    count: num_punches_earned_with_this_order,
                    punch_label,
                    punch_label_plural,
                });
            }
            return t('remaining_punches', {
                count: punches_left_to_earn,
                punch_label,
                punch_label_plural,
            });
        };

        render = () => {
            if (
                Customer.getActiveLoyaltyProgram() !== 'PunchCard' ||
                !this.state.is_customer_logged_in
            ) {
                return null;
            }

            const base_class = 'm2g-order-loyalty-punch-card';
            const {
                loyalty,
                order_punchcard_progress,
                is_order_confirmed,
            } = this.state;
            const num_punches_earned_with_this_order = is_order_confirmed
                ? 0
                : order_punchcard_progress &&
                  order_punchcard_progress.punches_earned;
            const rewards_earned_with_this_order = order_punchcard_progress
                ? order_punchcard_progress.rewards_earned
                : [];
            const total_num_punches_earned_with_this_order =
                num_punches_earned_with_this_order + loyalty.state.punches;
            const should_display_progress =
                loyalty.state.punches > 0 ||
                total_num_punches_earned_with_this_order > 0 ||
                is_order_confirmed;

            if (
                this.props.is_showing_in_confirmation &&
                rewards_earned_with_this_order.length === 0 &&
                order_punchcard_progress.punches_earned === 0
            ) {
                return null;
            }
            const { t } = this.props;

            const punch_label = t('punch_label');
            const punch_label_plural = t('punch_label_plural');

            return (
                <div
                    className={classNames(`${base_class}`)}
                    data-rewards-earned={rewards_earned_with_this_order.length}
                    data-punches-needed={
                        loyalty.program.settings.num_punches_needed
                    }
                    data-punches-earned-with-this-order={
                        num_punches_earned_with_this_order
                    }
                >
                    {should_display_progress && (
                        <div
                            className={classNames(
                                `${base_class}-progress-message m2g-text--small`
                            )}
                        >
                            {this.getProgressMessage()}
                        </div>
                    )}

                    <div
                        className={classNames(
                            `${base_class}-program-container`
                        )}
                    >
                        <div
                            className={classNames(
                                `${base_class}-icon m2g-icon m2g-icon--award m2g-icon--medium`
                            )}
                        />
                        <div className={classNames(`${base_class}-title`)}>
                            {this.props.t('title')}
                        </div>
                        {loyalty.program.description && (
                            <div
                                className={classNames(
                                    `${base_class}-description m2g-text--offset m2g-text--small`
                                )}
                            >
                                {this.props.t('description', {
                                    punch_label,
                                })}
                            </div>
                        )}
                        {t('terms_and_conditions') && (
                            <div
                                className={classNames(
                                    `${base_class}-terms-and-conditions m2g-text--link m2g-text--offset m2g-text--small`
                                )}
                                onClick={this.clickTermsAndConditions}
                            >
                                {t('terms_and_conditions')}
                            </div>
                        )}
                    </div>

                    {should_display_progress && (
                        <div
                            className={classNames(
                                `${base_class}-punches-container`
                            )}
                        >
                            <div
                                className={classNames(`${base_class}-punches`)}
                            >
                                {_.map(
                                    _.range(
                                        loyalty.program.settings
                                            .num_punches_needed
                                    ),
                                    num => {
                                        num += 1;
                                        let is_punched =
                                            num <= loyalty.state.punches;

                                        // Special case for reward earned state:
                                        // We want to show the customer a fully-punched card.
                                        // Technically, a punchcard cannot be 'seen' in a 'completed state' because
                                        // as soon as the punchcard has been 'completed' (i.e. all punches ticked off)
                                        // the API will roll over to a new card and report the state of the new card.
                                        if (
                                            is_order_confirmed &&
                                            rewards_earned_with_this_order.length >
                                                0
                                        ) {
                                            is_punched = true;
                                        }

                                        return (
                                            <div
                                                key={num}
                                                className={classNames(
                                                    `${base_class}-punch`
                                                )}
                                                data-punch={num}
                                                data-pause-animation={
                                                    this.state.pause_animation
                                                }
                                                data-is-reward={false}
                                                data-is-punched={is_punched}
                                                data-is-punched-with-current-order={
                                                    !is_punched &&
                                                    num <=
                                                        total_num_punches_earned_with_this_order
                                                }
                                            >
                                                <div
                                                    className={classNames(
                                                        `${base_class}-punch-content`
                                                    )}
                                                >
                                                    <div
                                                        className={classNames(
                                                            `${base_class}-punch-content ${base_class}-punch-content--number`
                                                        )}
                                                    >
                                                        {num}
                                                    </div>
                                                    <span
                                                        className={classNames(
                                                            `${base_class}-punch-content ${base_class}-punch-content--tick m2g-icon m2g-icon--tick`
                                                        )}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    }
                                )}
                                <div
                                    className={classNames(
                                        `${base_class}-punch`
                                    )}
                                    data-pause-animation={
                                        this.state.pause_animation
                                    }
                                    data-is-reward
                                >
                                    <div
                                        className={classNames(
                                            `${base_class}-punch-content`
                                        )}
                                    >
                                        <span
                                            className={classNames(
                                                `${base_class}-punch-content ${base_class}-punch-content--reward m2g-icon m2g-icon--gift`
                                            )}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div
                                className={classNames(
                                    `${base_class}-punch-count-progress`
                                )}
                            >
                                <div
                                    className={classNames(
                                        `${base_class}-punch-count-progress-number-container`
                                    )}
                                >
                                    <span
                                        className={classNames(
                                            `${base_class}-punch-count-progress-number`
                                        )}
                                    >
                                        {loyalty.state.punches}
                                    </span>
                                    &nbsp;
                                    <span
                                        className={classNames(
                                            `${base_class}-punch-count-progress-number-label`
                                        )}
                                    >
                                        {loyalty.state.punches > 1
                                            ? punch_label_plural
                                            : punch_label}
                                    </span>
                                </div>

                                <div
                                    className={classNames(
                                        `${base_class}-punch-count-progress-bar`
                                    )}
                                >
                                    <div
                                        className={classNames(
                                            `${base_class}-punch-count-progress-bar-label`
                                        )}
                                    >
                                        {loyalty.program.title}
                                    </div>
                                    <div
                                        className={classNames(
                                            `${base_class}-punch-count-progress-bar-container`
                                        )}
                                    >
                                        <div
                                            className={classNames(
                                                `${base_class}-punch-count-progress-bar-content ${base_class}-punch-count-progress-bar-content--earned`
                                            )}
                                            style={{
                                                width: `${(loyalty.state
                                                    .punches /
                                                    loyalty.program.settings
                                                        .num_punches_needed) *
                                                    100}%`,
                                            }}
                                        />
                                        <div
                                            className={classNames(
                                                `${base_class}-punch-count-progress-bar-content ${base_class}-punch-count-progress-bar-content--earned-with-this-order`
                                            )}
                                            style={{
                                                width: `${((loyalty.state
                                                    .punches +
                                                    num_punches_earned_with_this_order) /
                                                    loyalty.program.settings
                                                        .num_punches_needed) *
                                                    100}%`,
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            );
        };
    }
);
